import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axios';

export const LockContext = createContext();

export const LockProvider = ({ children }) => {
  const [locked, setLocked] = useState(sessionStorage.getItem('locked') === 'true');
  const [checkTimer, setCheckTimer] = useState(null);
  const { pathname } = useLocation();
  
  // useRef를 사용하여 렌더링을 유발하지 않는 값 관리
  const lastActivityRef = useRef(Date.now());

  // 유휴 시간(밀리초)
  const TIMEOUT = 30 * 60 * 1000; // 30분
  // 상태 체크 간격(밀리초) - 10초마다 한 번씩만 체크
  const CHECK_INTERVAL = 10 * 1000;

  // 사용자 활동 감지 이벤트 핸들러
  const handleUserActivity = useCallback(() => {
    // setState 대신 ref 업데이트 - 렌더링 발생 없음
    lastActivityRef.current = Date.now();
  }, []);

  // 잠금 상태 체크 함수
  const checkLockStatus = useCallback(() => {
    // ref 값을 사용하여 상태 업데이트 없이 현재 값 접근
    const now = Date.now();
    const idleTime = now - lastActivityRef.current;
    
    // 유휴 시간이 TIMEOUT을 초과한 경우 클라이언트 측에서 자동 잠금
    if (idleTime > TIMEOUT) {
      setLocked(true);
      sessionStorage.setItem('locked', 'true');
      return;
    }
    
    // 다음 체크 예약
    const timer = setTimeout(checkLockStatus, CHECK_INTERVAL);
    setCheckTimer(timer);
  }, []);

  // 사용자 활동 이벤트 리스너 설정
  useEffect(() => {
    if (locked || pathname === '/login') return;
    
    // 이벤트 리스너 등록
    const events = ['click', 'mousemove', 'keydown', 'scroll'];
    events.forEach(evt => document.addEventListener(evt, handleUserActivity));
    
    // 초기 활동 시간 설정 및 체크 타이머 시작
    lastActivityRef.current = Date.now();
    const timer = setTimeout(checkLockStatus, CHECK_INTERVAL);
    setCheckTimer(timer);
    
    // 클린업 함수
    return () => {
      events.forEach(evt => document.removeEventListener(evt, handleUserActivity));
      if (checkTimer) clearTimeout(checkTimer);
    };
  }, [pathname, locked, handleUserActivity, checkLockStatus]);

  const lock = () => {
    if (checkTimer) clearTimeout(checkTimer);
    setLocked(true);
    sessionStorage.setItem('locked', 'true');
  };

  const unlock = async (password) => {
    try {
      const response = await axiosInstance.post('/auth/verify-password', {
        password,
      });

      if (response.data.success) {
        setLocked(false);
        sessionStorage.removeItem('locked');
        // 활동 시간 초기화 및 타이머 재시작
        lastActivityRef.current = Date.now();
        const timer = setTimeout(checkLockStatus, CHECK_INTERVAL);
        setCheckTimer(timer);
        return { success: true };
      }
      
      // 로그아웃되었거나 세션이 만료된 경우(440) 또는 강제 로그아웃된 경우(441)
      const errorCode = parseInt(response.data.error_code || 0);
      if (errorCode === 440 || errorCode === 441) {
        return { 
          success: false, 
          error: 'session_expired', 
          message: response.data.message || '세션이 만료되었습니다. 다시 로그인해주세요.'
        };
      }
      
      // 기타 오류의 경우
      return { 
        success: false, 
        error: 'invalid_password',
        message: response.data.message || '비밀번호가 일치하지 않습니다.'
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'unknown_error',
        message: error.response?.data?.message || '알 수 없는 오류가 발생했습니다.'
      };
    }
  };

  return (
    <LockContext.Provider value={{ locked, lock, unlock }}>
      {children}
    </LockContext.Provider>
  );
};
